import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { organisationApi } from '@/api/organisation';
import { useAsyncData, type AsyncState } from '@/hooks';
import type { Campus, InstitutionUser } from '@/types';
import type {
  AcademicRank,
  AcademicRankAssignment,
  AppointmentRegisterRow,
  EducationSubsystem,
  InstitutionPositionView,
  OrganisationalUnitList,
  Paginated,
  PositionScopeType,
  RoleBundle,
} from '@/types/organisation';
import {
  EMPTY_DRAFT,
  buildAppointmentPayload,
  validateAppointmentDraft,
  type AppointmentDraft,
  type AppointmentErrors,
} from './appointment-form';
import { describeWriteFailure } from './PositionsTab';
import { AsyncPanel, FailureNotice } from '@/components/platform/AsyncPanel';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import { Checkbox } from '@/components/ui/Checkbox';
import { useToast } from '@/components/ui/Toast';
import styles from '../AdminOrganisationPage.module.css';

const NS = 'admin.organisation';

/** The register statuses identity files an assignment under. */
const STATUS_FILTERS = ['PENDING', 'ACTIVE', 'SUSPENDED', 'EXPIRED', 'ENDED', 'REVOKED'] as const;

const PAGE_SIZE = 20;

/**
 * The Appointments tab — the institution's whole register, plus the one write that
 * matters: recording an appointment as ONE transaction (position + optional rank +
 * optional permission bundle), so nobody is left holding an office without the access
 * that was meant to come with it.
 */

interface AppointmentsTabProps {
  institutionId: string;
  locale: string;
  subsystem: EducationSubsystem;
  positionsState: AsyncState<InstitutionPositionView[]>;
  users: InstitutionUser[];
  campuses: Campus[];
  ranks: AcademicRank[];
  bundles: RoleBundle[];
}

export function AppointmentsTab({
  institutionId,
  locale,
  subsystem,
  positionsState,
  users,
  campuses,
  ranks,
  bundles,
}: AppointmentsTabProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [status, setStatus] = useState('');
  const [positionCode, setPositionCode] = useState('');
  const [scopeType, setScopeType] = useState('');
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [endTarget, setEndTarget] = useState<AppointmentRegisterRow | null>(null);
  const [approving, setApproving] = useState<string | null>(null);

  const positions = positionsState.status === 'ready' ? positionsState.data : [];

  const { state: registerState, reload: reloadRegister } = useAsyncData<Paginated<AppointmentRegisterRow>>(
    `org-register-${institutionId}-${status}-${positionCode}-${scopeType}-${locale}-${subsystem}-${page}`,
    () =>
      organisationApi.appointments.register(institutionId, {
        status: status || undefined,
        positionCode: positionCode || undefined,
        scopeType: (scopeType || undefined) as PositionScopeType | undefined,
        locale,
        subsystem,
        page,
        limit: PAGE_SIZE,
      }),
    !!institutionId,
  );

  // Names for rows whose personName the server left blank: the console already holds
  // the member list, and an id column nobody can read is not a register.
  const nameByPersonId = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of users) map.set(user.identityId ?? user.id, user.name);
    return map;
  }, [users]);

  // Every scope type any position in the catalogue can be held over — the filter only
  // offers values that can actually occur in this institution's register.
  const scopeTypeOptions = useMemo(() => {
    const seen = new Set<PositionScopeType>();
    for (const position of positions) for (const scope of position.assignableScopeTypes) seen.add(scope);
    return [...seen];
  }, [positions]);

  const approve = async (row: AppointmentRegisterRow) => {
    setApproving(row.id);
    try {
      await organisationApi.positions.approveAssignment(row.id);
      toast.success(t(`${NS}.register.approvedToast`));
      reloadRegister();
    } catch (cause) {
      toast.error(describeWriteFailure(cause, t));
    } finally {
      setApproving(null);
    }
  };

  return (
    <section className={styles.section}>
      <div className={styles.registerToolbar}>
        <div className={styles.registerFilters}>
          <FormField id="register-status" label={t(`${NS}.register.filterStatus`)}>
            <Select
              id="register-status"
              value={status}
              onChange={(value) => {
                setStatus(value);
                setPage(1);
              }}
              placeholder={t(`${NS}.register.filterAny`)}
              options={[
                { value: '', label: t(`${NS}.register.filterAny`) },
                ...STATUS_FILTERS.map((value) => ({
                  value,
                  label: t(`${NS}.statuses.${value}`, { defaultValue: value }),
                })),
              ]}
            />
          </FormField>
          <FormField id="register-position" label={t(`${NS}.register.filterPosition`)}>
            <Select
              id="register-position"
              searchable
              value={positionCode}
              onChange={(value) => {
                setPositionCode(value);
                setPage(1);
              }}
              placeholder={t(`${NS}.register.filterAny`)}
              options={[
                { value: '', label: t(`${NS}.register.filterAny`) },
                ...positions.map((position) => ({
                  value: position.canonicalCode,
                  label: position.displayTitle,
                  description: position.canonicalCode,
                })),
              ]}
            />
          </FormField>
          <FormField id="register-scope" label={t(`${NS}.register.filterScope`)}>
            <Select
              id="register-scope"
              value={scopeType}
              onChange={(value) => {
                setScopeType(value);
                setPage(1);
              }}
              placeholder={t(`${NS}.register.filterAny`)}
              options={[
                { value: '', label: t(`${NS}.register.filterAny`) },
                ...scopeTypeOptions.map((value) => ({
                  value,
                  label: t(`${NS}.scopeTypes.${value}`, { defaultValue: value }),
                })),
              ]}
            />
          </FormField>
        </div>
        <Button onClick={() => setFormOpen(true)}>{t(`${NS}.register.new`)}</Button>
      </div>

      <Card>
        <AsyncPanel
          state={registerState}
          onRetry={reloadRegister}
          isEmpty={(data) => data.data.length === 0}
          emptyTitle={t(`${NS}.register.empty`)}
          emptyBody={
            status || positionCode || scopeType
              ? t(`${NS}.register.emptyFiltered`)
              : t(`${NS}.register.emptyBody`)
          }
        >
          {(pageData) => (
            <>
              <div className={styles.tableScroll}>
                <table className={styles.registerTable}>
                  <thead>
                    <tr>
                      <th scope="col">{t(`${NS}.register.person`)}</th>
                      <th scope="col">{t(`${NS}.register.office`)}</th>
                      <th scope="col">{t(`${NS}.register.scope`)}</th>
                      <th scope="col">{t(`${NS}.register.rankBundle`)}</th>
                      <th scope="col">{t(`${NS}.register.validity`)}</th>
                      <th scope="col">{t(`${NS}.register.status`)}</th>
                      <th scope="col">
                        <span className={styles.srOnly}>{t(`${NS}.register.actions`)}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.data.map((row) => (
                      <RegisterRow
                        key={row.id}
                        row={row}
                        personName={row.personName || nameByPersonId.get(row.personId) || row.personId}
                        locale={locale}
                        approving={approving === row.id}
                        onApprove={() => approve(row)}
                        onEnd={() => setEndTarget(row)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className={styles.pager}>
                <span className={styles.pagerInfo}>
                  {t(`${NS}.register.pageInfo`, {
                    page: pageData.page,
                    totalPages: Math.max(1, pageData.totalPages),
                    total: pageData.total,
                  })}
                </span>
                <div className={styles.pagerButtons}>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={pageData.page <= 1}
                    onClick={() => setPage(pageData.page - 1)}
                  >
                    {t('common.previous')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={pageData.page >= pageData.totalPages}
                    onClick={() => setPage(pageData.page + 1)}
                  >
                    {t('common.next')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </AsyncPanel>
      </Card>

      {formOpen && (
        <NewAppointmentModal
          institutionId={institutionId}
          locale={locale}
          positions={positions}
          users={users}
          campuses={campuses}
          ranks={ranks}
          bundles={bundles}
          onClose={() => setFormOpen(false)}
          onDone={() => {
            setFormOpen(false);
            reloadRegister();
          }}
        />
      )}

      {endTarget && (
        <EndAppointmentModal
          row={endTarget}
          personName={endTarget.personName || nameByPersonId.get(endTarget.personId) || endTarget.personId}
          onClose={() => setEndTarget(null)}
          onDone={() => {
            setEndTarget(null);
            reloadRegister();
          }}
        />
      )}
    </section>
  );
}

function RegisterRow({
  row,
  personName,
  locale,
  approving,
  onApprove,
  onEnd,
}: {
  row: AppointmentRegisterRow;
  personName: string;
  locale: string;
  approving: boolean;
  onApprove: () => void;
  onEnd: () => void;
}) {
  const { t } = useTranslation();
  const scopeLabel =
    row.scopeDisplayName ||
    row.scopeReference ||
    t(`${NS}.scopeTypes.${row.scopeType}`, { defaultValue: row.scopeType });
  const rankName = row.rank
    ? locale === 'fr' && row.rank.displayNameFr
      ? row.rank.displayNameFr
      : row.rank.displayName
    : null;
  const canApprove = (row.approvalStatus ?? '').toUpperCase() === 'PENDING';
  const canEnd = row.status === 'ACTIVE' || row.status === 'PENDING' || row.status === 'SUSPENDED';

  return (
    <tr>
      <td>
        <span className={styles.appointmentPerson}>{personName}</span>
      </td>
      <td>
        <div className={styles.cellStack}>
          <span>{row.position.displayTitle}</span>
          <code className={styles.canonicalCode}>{row.position.canonicalCode}</code>
        </div>
      </td>
      <td>
        <div className={styles.cellStack}>
          <span>{scopeLabel}</span>
          <span className={styles.cellMuted}>
            {t(`${NS}.scopeTypes.${row.scopeType}`, { defaultValue: row.scopeType })}
          </span>
        </div>
      </td>
      <td>
        <div className={styles.chipRow}>
          {row.acting && <Badge tone="warning">{t(`${NS}.appointments.acting`)}</Badge>}
          {rankName && <Badge tone="neutral">{rankName}</Badge>}
          {row.bundle && <Badge tone="info">{row.bundle.displayName}</Badge>}
          {!row.acting && !rankName && !row.bundle && <span className={styles.cellMuted}>—</span>}
        </div>
      </td>
      <td>
        <span className={styles.cellMuted}>
          {row.validUntil
            ? t(`${NS}.appointments.between`, { from: row.validFrom, to: row.validUntil })
            : t(`${NS}.appointments.openEnded`, { from: row.validFrom })}
        </span>
      </td>
      <td>
        <div className={styles.chipRow}>
          <Badge tone={row.status === 'ACTIVE' ? 'success' : 'neutral'}>
            {t(`${NS}.statuses.${row.status}`, { defaultValue: row.status })}
          </Badge>
          {canApprove && <Badge tone="warning">{t(`${NS}.register.awaitingApproval`)}</Badge>}
        </div>
      </td>
      <td>
        <div className={styles.rowActions}>
          {canApprove && (
            <Button size="sm" variant="secondary" loading={approving} onClick={onApprove}>
              {t(`${NS}.register.approve`)}
            </Button>
          )}
          {canEnd && (
            <Button size="sm" variant="ghost" onClick={onEnd}>
              {t(`${NS}.register.end`)}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

/** End an appointment, with an optional recorded reason. */
function EndAppointmentModal({
  row,
  personName,
  onClose,
  onDone,
}: {
  row: AppointmentRegisterRow;
  personName: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [endReason, setEndReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await organisationApi.positions.endAssignment(row.id, endReason.trim() || undefined);
      toast.success(t(`${NS}.register.endedToast`, { person: personName }));
      onDone();
    } catch (cause) {
      setError(describeWriteFailure(cause, t));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title={t(`${NS}.register.endConfirmTitle`, { person: personName, title: row.position.displayTitle })}
      description={t(`${NS}.register.endConfirmBody`)}
      onClose={onClose}
    >
      <div className={styles.dialogBody}>
        {error && <Alert tone="error">{error}</Alert>}
        <FormField
          id="end-reason"
          label={t(`${NS}.register.endReason`)}
          hint={t(`${NS}.positions.reasonHint`)}
        >
          <input
            id="end-reason"
            className={styles.textInput}
            type="text"
            value={endReason}
            maxLength={500}
            onChange={(event) => setEndReason(event.target.value)}
          />
        </FormField>
        <div className={styles.dialogActions}>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={submit} loading={busy} loadingLabel={t('common.submitting')}>
            {t(`${NS}.register.end`)}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * The one-transaction appointment form.
 *
 * Every server rule is mirrored client-side BEFORE submit (see appointment-form.ts):
 * position options are only the active offices, scope types only what the chosen
 * office may be held over, a sub-institution scope demands a unit, an acting
 * appointment demands an end date, and the end must fall after the start. The server
 * still gets the last word; its refusals are shown verbatim in the dialog.
 */
function NewAppointmentModal({
  institutionId,
  locale,
  positions,
  users,
  campuses,
  ranks,
  bundles,
  onClose,
  onDone,
}: {
  institutionId: string;
  locale: string;
  positions: InstitutionPositionView[];
  users: InstitutionUser[];
  campuses: Campus[];
  ranks: AcademicRank[];
  bundles: RoleBundle[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const [draft, setDraft] = useState<AppointmentDraft>({ ...EMPTY_DRAFT, validFrom: today });
  const [errors, setErrors] = useState<AppointmentErrors>({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const set = <K extends keyof AppointmentDraft>(key: K, value: AppointmentDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    // Clearing a field's error the moment it changes: stale red text under a field
    // the user just fixed teaches them to ignore red text.
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  // Only offices this institution actually has switched on may be offered — mirroring
  // the server's check instead of letting the form offer something it will refuse.
  const activePositions = useMemo(() => positions.filter((p) => p.active), [positions]);
  const position = activePositions.find((p) => p.canonicalCode === draft.positionCode) ?? null;
  const scopeChoices = position?.assignableScopeTypes ?? [];
  const needsUnit =
    !!draft.scopeType && draft.scopeType !== 'INSTITUTION' && draft.scopeType !== 'CAMPUS';

  /**
   * The scope directory, fetched per scope type from EduCore through identity.
   * A 503 is "EduCore unreachable" and renders as exactly that, with a retry —
   * never as an empty list of departments.
   */
  const { state: unitsState, reload: reloadUnits } = useAsyncData<OrganisationalUnitList>(
    `org-units-${institutionId}-${draft.scopeType}`,
    () => organisationApi.units.list(institutionId, { type: draft.scopeType || undefined }),
    needsUnit,
  );

  /** What the person already holds — shown beside the rank field so conferring is informed. */
  const { state: personRanksState } = useAsyncData<AcademicRankAssignment[]>(
    `org-person-ranks-${draft.personId}`,
    () => organisationApi.ranks.forPerson(draft.personId),
    !!draft.personId && ranks.length > 0,
  );

  const currentRankName = useMemo(() => {
    if (personRanksState.status !== 'ready') return null;
    const current = personRanksState.data.find((row) => !row.effectiveUntil || row.effectiveUntil >= today);
    if (!current) return null;
    const rank = ranks.find((r) => r.id === current.academicRankId);
    if (!rank) return null;
    return locale === 'fr' && rank.displayNameFr ? rank.displayNameFr : rank.displayName;
  }, [personRanksState, ranks, locale, today]);

  const submit = async () => {
    const found = validateAppointmentDraft(draft, position);
    setErrors(found);
    if (Object.values(found).some(Boolean)) return;
    setBusy(true);
    setServerError(null);
    try {
      await organisationApi.appointments.create(institutionId, buildAppointmentPayload(draft));
      toast.success(t(`${NS}.form.createdToast`));
      onDone();
    } catch (cause) {
      setServerError(describeWriteFailure(cause, t));
      setBusy(false);
    }
  };

  const fieldError = (key: keyof AppointmentErrors) => {
    const code = errors[key];
    return code ? t(`${NS}.formErrors.${code}`) : undefined;
  };

  return (
    <Modal open title={t(`${NS}.form.title`)} description={t(`${NS}.form.subtitle`)} onClose={onClose}>
      <div className={styles.dialogBody}>
        {serverError && <Alert tone="error">{serverError}</Alert>}

        <FormField
          id="appointment-person"
          label={t(`${NS}.form.person`)}
          required
          error={fieldError('personId')}
        >
          <Select
            id="appointment-person"
            searchable
            required
            invalid={!!errors.personId}
            value={draft.personId}
            onChange={(value) => set('personId', value)}
            placeholder={t(`${NS}.appointments.selectPerson`)}
            options={users.map((user) => ({
              // identityId is the person UUID identity keys on; the row id is core's
              // projection id and is not the same value.
              value: user.identityId ?? user.id,
              label: user.name,
              description: user.email,
            }))}
          />
        </FormField>

        <FormField
          id="appointment-position"
          label={t(`${NS}.form.position`)}
          hint={t(`${NS}.form.positionHint`)}
          required
          error={fieldError('positionCode')}
        >
          <Select
            id="appointment-position"
            searchable
            required
            invalid={!!errors.positionCode}
            value={draft.positionCode}
            onChange={(value) => {
              set('positionCode', value);
              // A scope chosen for the previous office may not be legal for this one.
              const next = activePositions.find((p) => p.canonicalCode === value);
              const scopes = next?.assignableScopeTypes ?? [];
              setDraft((current) => ({
                ...current,
                positionCode: value,
                scopeType: scopes.length === 1 ? scopes[0] : scopes.includes(current.scopeType as PositionScopeType) ? current.scopeType : '',
                scopeReference: '',
                scopeDisplayName: '',
                campusId: '',
              }));
            }}
            placeholder={t(`${NS}.form.positionPlaceholder`)}
            options={activePositions.map((p) => ({
              value: p.canonicalCode,
              label: p.displayTitle,
              description: p.canonicalCode,
            }))}
          />
        </FormField>

        <FormField
          id="appointment-scope-type"
          label={t(`${NS}.form.scopeType`)}
          hint={position ? t(`${NS}.form.scopeTypeHint`, { title: position.displayTitle }) : undefined}
          required
          error={fieldError('scopeType')}
        >
          <Select
            id="appointment-scope-type"
            required
            invalid={!!errors.scopeType}
            disabled={!position}
            value={draft.scopeType}
            onChange={(value) => {
              setDraft((current) => ({
                ...current,
                scopeType: value as PositionScopeType,
                scopeReference: '',
                scopeDisplayName: '',
                campusId: '',
              }));
              setErrors((current) => ({ ...current, scopeType: undefined, scopeReference: undefined }));
            }}
            placeholder={t(`${NS}.form.scopeTypePlaceholder`)}
            options={scopeChoices.map((scope) => ({
              value: scope,
              label: t(`${NS}.scopeTypes.${scope}`, { defaultValue: scope }),
            }))}
          />
        </FormField>

        {draft.scopeType === 'CAMPUS' && (
          <FormField
            id="appointment-campus"
            label={t(`${NS}.form.campus`)}
            required
            error={fieldError('scopeReference')}
          >
            <Select
              id="appointment-campus"
              searchable
              required
              invalid={!!errors.scopeReference}
              value={draft.scopeReference}
              onChange={(value) => {
                const campus = campuses.find((c) => c.id === value);
                setDraft((current) => ({
                  ...current,
                  scopeReference: value,
                  campusId: value,
                  scopeDisplayName: campus?.name ?? '',
                }));
                setErrors((current) => ({ ...current, scopeReference: undefined }));
              }}
              placeholder={t(`${NS}.form.campusPlaceholder`)}
              options={campuses.map((campus) => ({ value: campus.id, label: campus.name }))}
            />
          </FormField>
        )}

        {needsUnit && (
          <FormField
            id="appointment-unit"
            label={t(`${NS}.form.unit`)}
            hint={t(`${NS}.form.unitHint`)}
            required
            error={fieldError('scopeReference')}
          >
            {unitsState.status === 'error' ? (
              unitsState.error.status === 503 ? (
                // 503 is "identity could not reach EduCore" — a named outage with a
                // retry, deliberately distinct from "this institution has no units".
                <Alert tone="error" title={t(`${NS}.form.educoreDownTitle`)}>
                  <p className={styles.alertBody}>{t(`${NS}.form.educoreDownBody`)}</p>
                  <Button size="sm" variant="secondary" onClick={reloadUnits}>
                    {t('common.retry')}
                  </Button>
                </Alert>
              ) : (
                <FailureNotice error={unitsState.error} onRetry={reloadUnits} />
              )
            ) : (
              <Select
                id="appointment-unit"
                searchable
                required
                invalid={!!errors.scopeReference}
                disabled={unitsState.status === 'loading'}
                value={draft.scopeReference}
                onChange={(value) => {
                  const unit =
                    unitsState.status === 'ready'
                      ? unitsState.data.items.find((item) => item.id === value)
                      : undefined;
                  setDraft((current) => ({
                    ...current,
                    scopeReference: value,
                    scopeDisplayName: unit?.name ?? '',
                  }));
                  setErrors((current) => ({ ...current, scopeReference: undefined }));
                }}
                placeholder={
                  unitsState.status === 'loading'
                    ? t('common.loading')
                    : t(`${NS}.form.unitPlaceholder`)
                }
                emptyLabel={t(`${NS}.form.unitEmpty`)}
                options={
                  unitsState.status === 'ready'
                    ? unitsState.data.items.map((unit) => ({
                        value: unit.id,
                        label: unit.name,
                        description: unit.code ?? undefined,
                      }))
                    : []
                }
              />
            )}
          </FormField>
        )}

        <div className={styles.formRow}>
          <FormField
            id="appointment-valid-from"
            label={t(`${NS}.form.validFrom`)}
            required
            error={fieldError('validFrom')}
          >
            <input
              id="appointment-valid-from"
              className={styles.textInput}
              type="date"
              required
              aria-invalid={!!errors.validFrom || undefined}
              value={draft.validFrom}
              onChange={(event) => set('validFrom', event.target.value)}
            />
          </FormField>
          <FormField
            id="appointment-valid-until"
            label={t(`${NS}.form.validUntil`)}
            hint={draft.acting ? t(`${NS}.form.validUntilActingHint`) : t(`${NS}.form.validUntilHint`)}
            required={draft.acting}
            error={fieldError('validUntil')}
          >
            <input
              id="appointment-valid-until"
              className={styles.textInput}
              type="date"
              required={draft.acting}
              aria-invalid={!!errors.validUntil || undefined}
              min={draft.validFrom || undefined}
              value={draft.validUntil}
              onChange={(event) => set('validUntil', event.target.value)}
            />
          </FormField>
        </div>

        <Checkbox
          id="appointment-acting"
          checked={draft.acting}
          onChange={(event) => set('acting', event.target.checked)}
          label={t(`${NS}.form.acting`)}
          description={t(`${NS}.form.actingHint`)}
        />

        <FormField
          id="appointment-rank"
          label={t(`${NS}.form.rank`)}
          // Where a rank is conferred, the page says what a rank is not.
          hint={
            currentRankName
              ? `${t(`${NS}.form.rankHint`)} ${t(`${NS}.form.currentRank`, { rank: currentRankName })}`
              : t(`${NS}.form.rankHint`)
          }
        >
          <Select
            id="appointment-rank"
            value={draft.academicRankId}
            onChange={(value) => set('academicRankId', value)}
            placeholder={t(`${NS}.form.rankPlaceholder`)}
            options={[
              { value: '', label: t(`${NS}.form.rankNone`) },
              ...ranks
                .filter((rank) => rank.active)
                .map((rank) => ({
                  value: rank.id,
                  label: locale === 'fr' && rank.displayNameFr ? rank.displayNameFr : rank.displayName,
                  description: rank.code,
                })),
            ]}
          />
        </FormField>

        {draft.academicRankId && (
          <FormField
            id="appointment-rank-evidence"
            label={t(`${NS}.form.rankEvidence`)}
            hint={t(`${NS}.form.rankEvidenceHint`)}
          >
            <input
              id="appointment-rank-evidence"
              className={styles.textInput}
              type="text"
              maxLength={200}
              value={draft.rankEvidenceReference}
              onChange={(event) => set('rankEvidenceReference', event.target.value)}
            />
          </FormField>
        )}

        <FormField id="appointment-bundle" label={t(`${NS}.form.bundle`)} hint={t(`${NS}.form.bundleHint`)}>
          <Select
            id="appointment-bundle"
            searchable
            value={draft.roleDefinitionId}
            onChange={(value) => set('roleDefinitionId', value)}
            placeholder={t(`${NS}.form.bundlePlaceholder`)}
            options={[
              { value: '', label: t(`${NS}.form.bundleNone`) },
              ...bundles
                .filter((bundle) => bundle.active)
                .map((bundle) => ({
                  value: bundle.id,
                  label: bundle.displayName,
                  description: bundle.code,
                })),
            ]}
          />
        </FormField>

        <FormField id="appointment-reason" label={t(`${NS}.positions.reasonLabel`)} hint={t(`${NS}.positions.reasonHint`)}>
          <input
            id="appointment-reason"
            className={styles.textInput}
            type="text"
            maxLength={500}
            value={draft.reason}
            onChange={(event) => set('reason', event.target.value)}
          />
        </FormField>

        {position?.requiresApproval && (
          <Alert tone="info">{t(`${NS}.form.requiresApprovalNote`)}</Alert>
        )}

        <div className={styles.dialogActions}>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} loading={busy} loadingLabel={t('common.submitting')}>
            {t(`${NS}.form.submit`)}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
